'use client'

/**
 * Client Error Reporter (Issue #5520)
 *
 * Installs window-level `error` and `unhandledrejection` handlers that
 * forward structured error info to the server for logging. This catches
 * errors that escape React's error boundaries — bare script errors,
 * unhandled promise rejections, and early-lifecycle failures before
 * the error boundary tree mounts.
 *
 * Renders nothing. Mounted near the root so it installs as early as
 * possible.
 *
 * Design notes:
 * - Uses `capture: true` on the 'error' listener so we see errors before
 *   other handlers swallow them.
 * - De-duplicates identical messages fired within a short window — React
 *   in dev mode double-invokes effects, and the same error can be raised
 *   repeatedly during render loops (#310 itself does this).
 * - Fire-and-forget: the server action result is ignored. If logging
 *   fails, we do not want to mask the underlying error.
 */

import { useEffect } from 'react'
import { logClientError } from '@/app/actions/log-client-error'

/** De-dupe window: identical messages within this window collapse to one */
const DEDUPE_WINDOW_MS = 1_000

export function ClientErrorReporter() {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const recent = new Map<string, number>()
    function isDuplicate(key: string): boolean {
      const now = Date.now()
      const last = recent.get(key)
      recent.set(key, now)
      // Cheap cleanup: if the map grows, drop old entries
      if (recent.size > 50) {
        for (const [k, v] of recent) {
          if (now - v > DEDUPE_WINDOW_MS * 5) recent.delete(k)
        }
      }
      return last != null && now - last < DEDUPE_WINDOW_MS
    }

    function handleError(event: ErrorEvent) {
      const message = event.message || event.error?.message || 'Unknown error'
      const key = `window-error:${message}`
      if (isDuplicate(key)) return
      logClientError({
        source: 'window-error',
        message,
        stack: event.error?.stack ?? null,
        componentStack: null,
        digest: null,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
      }).catch(() => {})
    }

    function handleRejection(event: PromiseRejectionEvent) {
      const reason = event.reason
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'Unhandled promise rejection'
      const stack = reason instanceof Error ? reason.stack : null
      const key = `unhandled-rejection:${message}`
      if (isDuplicate(key)) return
      logClientError({
        source: 'unhandled-rejection',
        message,
        stack: stack ?? null,
        componentStack: null,
        digest: null,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
      }).catch(() => {})
    }

    window.addEventListener('error', handleError, { capture: true })
    window.addEventListener('unhandledrejection', handleRejection)
    return () => {
      window.removeEventListener('error', handleError, { capture: true })
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])

  return null
}
